import React, { useEffect, useState } from 'react';
import researchService from '../../services/researchService';

/**
 * Modal for managing global preset questions shown in the Research chat panel.
 *
 * Props:
 *   onClose()  — called when the modal is dismissed (after any saves)
 */
export default function ResearchPresetQuestionsManager({ onClose }) {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // New question form
  const [newText, setNewText] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [saving, setSaving] = useState(false);

  // Inline edit state: { [question_id]: { text, label } }
  const [editState, setEditState] = useState({});
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    load();
  }, []);

  const load = () => {
    setLoading(true);
    setError(null);
    researchService
      .listPresetQuestions()
      .then(setQuestions)
      .catch(() => setError('Failed to load preset questions.'))
      .finally(() => setLoading(false));
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newText.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const nextOrder = questions.length > 0
        ? Math.max(...questions.map((q) => q.sort_order)) + 1
        : 0;
      const created = await researchService.createPresetQuestion({
        text: newText.trim(),
        label: newLabel.trim() || undefined,
        sort_order: nextOrder,
      });
      setQuestions((prev) => [...prev, created]);
      setNewText('');
      setNewLabel('');
    } catch {
      setError('Failed to add question.');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (q) => {
    setEditingId(q.question_id);
    setEditState({ text: q.text, label: q.label || '' });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditState({});
  };

  const handleSaveEdit = async (questionId) => {
    setSaving(true);
    setError(null);
    try {
      const updated = await researchService.updatePresetQuestion(questionId, {
        text: editState.text?.trim() || undefined,
        label: editState.label?.trim() || undefined,
      });
      setQuestions((prev) => prev.map((q) => (q.question_id === questionId ? updated : q)));
      setEditingId(null);
      setEditState({});
    } catch {
      setError('Failed to save changes.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (questionId) => {
    if (!window.confirm('Delete this preset question?')) return;
    setError(null);
    try {
      await researchService.deletePresetQuestion(questionId);
      setQuestions((prev) => prev.filter((q) => q.question_id !== questionId));
    } catch {
      setError('Failed to delete question.');
    }
  };

  const moveOrder = async (questionId, direction) => {
    const idx = questions.findIndex((q) => q.question_id === questionId);
    const swapIdx = idx + direction;
    if (swapIdx < 0 || swapIdx >= questions.length) return;

    const a = questions[idx];
    const b = questions[swapIdx];

    try {
      await Promise.all([
        researchService.updatePresetQuestion(a.question_id, { sort_order: b.sort_order }),
        researchService.updatePresetQuestion(b.question_id, { sort_order: a.sort_order }),
      ]);
      const reordered = [...questions];
      reordered[idx] = { ...a, sort_order: b.sort_order };
      reordered[swapIdx] = { ...b, sort_order: a.sort_order };
      setQuestions(reordered.sort((x, y) => x.sort_order - y.sort_order));
    } catch {
      setError('Failed to reorder.');
    }
  };

  return (
    <div
      className="modal d-block"
      style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="bi bi-list-check me-2" />Manage Preset Questions
            </h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>

          <div className="modal-body">
            {error && (
              <div className="alert alert-danger py-2">{error}</div>
            )}

            {loading ? (
              <div className="text-muted small">Loading…</div>
            ) : questions.length === 0 ? (
              <div className="text-muted small mb-3">No preset questions yet. Add one below.</div>
            ) : (
              <table className="table table-sm table-bordered align-middle mb-3">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: 80 }}>Order</th>
                    <th>Question text</th>
                    <th style={{ width: 140 }}>Short label</th>
                    <th style={{ width: 100 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {questions.map((q, idx) => (
                    <tr key={q.question_id}>
                      <td className="text-center">
                        <div className="d-flex flex-column align-items-center gap-1">
                          <button
                            className="btn btn-sm btn-outline-secondary p-0 px-1"
                            onClick={() => moveOrder(q.question_id, -1)}
                            disabled={idx === 0 || saving}
                            title="Move up"
                          >
                            <i className="bi bi-chevron-up" />
                          </button>
                          <button
                            className="btn btn-sm btn-outline-secondary p-0 px-1"
                            onClick={() => moveOrder(q.question_id, 1)}
                            disabled={idx === questions.length - 1 || saving}
                            title="Move down"
                          >
                            <i className="bi bi-chevron-down" />
                          </button>
                        </div>
                      </td>
                      {editingId === q.question_id ? (
                        <>
                          <td>
                            <input
                              className="form-control form-control-sm"
                              value={editState.text}
                              onChange={(e) => setEditState((s) => ({ ...s, text: e.target.value }))}
                            />
                          </td>
                          <td>
                            <input
                              className="form-control form-control-sm"
                              value={editState.label}
                              placeholder="Optional"
                              onChange={(e) => setEditState((s) => ({ ...s, label: e.target.value }))}
                            />
                          </td>
                          <td>
                            <div className="d-flex gap-1">
                              <button
                                className="btn btn-sm btn-primary"
                                onClick={() => handleSaveEdit(q.question_id)}
                                disabled={saving || !editState.text?.trim()}
                              >
                                Save
                              </button>
                              <button className="btn btn-sm btn-outline-secondary" onClick={cancelEdit}>
                                Cancel
                              </button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td style={{ fontSize: '0.9rem' }}>{q.text}</td>
                          <td className="text-muted" style={{ fontSize: '0.85rem' }}>
                            {q.label || <span className="fst-italic">—</span>}
                          </td>
                          <td>
                            <div className="d-flex gap-1">
                              <button
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => startEdit(q)}
                                disabled={saving}
                              >
                                <i className="bi bi-pencil" />
                              </button>
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleDelete(q.question_id)}
                                disabled={saving}
                              >
                                <i className="bi bi-trash" />
                              </button>
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <form onSubmit={handleAdd}>
              <h6 className="fw-semibold mb-2">Add a question</h6>
              <div className="mb-2">
                <label className="form-label small mb-1">Question text <span className="text-danger">*</span></label>
                <input
                  className="form-control form-control-sm"
                  placeholder="e.g. What was revenue last quarter?"
                  value={newText}
                  onChange={(e) => setNewText(e.target.value)}
                  disabled={saving}
                />
              </div>
              <div className="mb-3">
                <label className="form-label small mb-1">Short label <span className="text-muted">(shown on button — defaults to full text)</span></label>
                <input
                  className="form-control form-control-sm"
                  placeholder="e.g. Revenue last quarter"
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  disabled={saving}
                />
              </div>
              <button
                className="btn btn-primary btn-sm"
                type="submit"
                disabled={saving || !newText.trim()}
              >
                <i className="bi bi-plus-lg me-1" />Add question
              </button>
            </form>
          </div>

          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onClose}>Done</button>
          </div>
        </div>
      </div>
    </div>
  );
}
